/**
 * All unreads with message previews (Slack "All unreads", logic/shortcuts unreadPreview): for each unread conversation
 * the newest few unread messages (author, one line, time) and "N more". Fetched from the server per conversation when
 * the list shows it, again when new messages arrive there; kept while offline (the list then shows what it had).
 */
import type { Message } from "@bundle/contracts";
import { messagePreview } from "../logic/messages.ts";
import { UNREAD_PREVIEW_MAX, unreadPreview } from "../logic/shortcuts.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

/** Longest preview line. */
const PREVIEW_MAX = 280;
/** Messages fetched per conversation (enough to find the newest few unread among thread-only replies). */
const FETCH_LIMIT = 20;

export type UnreadPreviewRow = { id: string; authorId: string; author: string; text: string; at: number };

export type UnreadPreview = {
  status: "loading" | "ready" | "failed";
  rows: UnreadPreviewRow[];
  /** Unread messages not shown. */
  more: number;
};

export type UnreadsViewState = {
  /** By conversation id; absent = not asked yet. */
  previews: Record<string, UnreadPreview>;
};

export class UnreadsViewModel extends ViewModel<UnreadsViewState> {
  /** The lastMessageSeq each conversation's preview was fetched at (refetched when it moves). */
  private readonly fetchedAt = new Map<string, number>();

  constructor(private readonly ws: WorkspaceViewModel) {
    super({ previews: {} });
  }

  /** The list shows these conversations: fetch the previews that are missing or out of date. */
  show(channelIds: readonly string[]): void {
    const cache = this.ws.state.cache;
    if (cache === undefined) return;
    for (const id of channelIds) {
      const c = cache.channels[id];
      const ms = cache.memberships[id];
      if (c === undefined || ms === undefined) continue;
      if (this.fetchedAt.get(id) === c.lastMessageSeq && this.state.previews[id]?.status !== "failed") continue;
      this.fetchedAt.set(id, c.lastMessageSeq);
      const prev = this.state.previews[id];
      if (prev === undefined) this.setPreview(id, { status: "loading", rows: [], more: 0 });
      this.ws.deps.api.listMessages(this.ws.wsId, id, { limit: FETCH_LIMIT }).then(
        (r) => this.loaded(id, r.messages),
        () => {
          this.fetchedAt.delete(id);
          const cur = this.state.previews[id];
          if (!this.isDisposed && (cur === undefined || cur.status === "loading")) this.setPreview(id, { status: "failed", rows: [], more: 0 });
        },
      );
    }
  }

  private loaded(channelId: string, messages: Message[]): void {
    if (this.isDisposed) return;
    const cache = this.ws.state.cache;
    const c = cache?.channels[channelId];
    const ms = cache?.memberships[channelId];
    if (cache === undefined || c === undefined || ms === undefined) return;
    const { messages: shown, more } = unreadPreview(messages, ms.lastReadSeq, cache.meId, Math.max(0, c.lastMessageSeq - ms.lastReadSeq), UNREAD_PREVIEW_MAX);
    const ctx = { meId: cache.meId, members: cache.members, channels: cache.channels, userGroups: cache.userGroups ?? {} };
    this.setPreview(channelId, {
      status: "ready",
      rows: shown.map((m) => ({ id: m.id, authorId: m.authorId, author: cache.members[m.authorId]?.displayName ?? "", text: messagePreview(m, ctx, PREVIEW_MAX), at: m.createdAt })),
      more,
    });
  }

  private setPreview(channelId: string, p: UnreadPreview): void {
    this.update({ previews: { ...this.state.previews, [channelId]: p } });
  }
}
